import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Alert,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { subscriptionApi, getCompanyId } from './subscriptionApi';
import { MENU_LIST } from './menuList';

// menuKey → friendly label, for showing a plan's features (its included menus).
const MENU_LABELS = Object.fromEntries(MENU_LIST.map((m) => [m.key, m.label]));

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const currency = company?.currency ?? '₹';
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

// One label/value line in the checkout total box.
const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
    <span>{label}</span>
    <span>{value}</span>
  </Box>
);

// Auto-submit a POST form to CC Avenue. The browser leaves our app and lands
// on the gateway; secrets never touch the client (encRequest is opaque).
const submitToCCAvenue = (gatewayUrl, encRequest, accessCode) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = gatewayUrl;
  const add = (name, value) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  };
  add('encRequest', encRequest);
  add('access_code', accessCode);
  document.body.appendChild(form);
  form.submit();
};

export default function Subscription() {
  const companyId = getCompanyId();
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  // Payment dialog (CC Avenue integration point)
  const [payOpen, setPayOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Add-ons (Hotel companies only) selectable at checkout.
  const isHotel = (sessionStorage.getItem('propertType') || 'H') === 'H';
  const [addons, setAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const GST_RATE = 0.18;

  // Result banner after returning from a gateway (?status=success|failed|mismatch).
  const [payResult, setPayResult] = useState(null);

  const loadAll = useCallback(async () => {
    if (!companyId) return;
    try {
      const [st, pl, hi, ad] = await Promise.all([
        subscriptionApi.getStatus(companyId),
        // Only show plans for this company's module (H = Hotel+Resto, R = Resto).
        subscriptionApi.getPlans(true, sessionStorage.getItem('propertType') || 'H'),
        subscriptionApi.getHistory(companyId),
        // Add-ons are Hotel-only.
        isHotel ? subscriptionApi.getAddons(true) : Promise.resolve({ data: [] }),
      ]);
      setStatus(st.data);
      setPlans(pl.data || []);
      setHistory(hi.data || []);
      setAddons(ad.data || []);
    } catch (err) {
      console.error('Load subscription failed', err);
    }
  }, [companyId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Read the CC Avenue return status, show a banner, then clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    if (status) {
      setPayResult(
        status === 'success' ? 'success' : status === 'mismatch' ? 'mismatch' : 'failed',
      );
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const openPay = (plan) => {
    setSelectedPlan(plan);
    setSelectedAddons([]); // fresh selection per checkout
    setPayOpen(true);
  };

  const toggleAddon = (id) =>
    setSelectedAddons((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  // Live checkout totals: (plan + selected add-ons) + 18% GST.
  const planPrice = Number(selectedPlan?.price || 0);
  const addonSum = addons
    .filter((a) => selectedAddons.includes(a.addonId))
    .reduce((sum, a) => sum + Number(a.price || 0), 0);
  const subtotal = planPrice + addonSum;
  const gstAmount = subtotal * GST_RATE;
  const grandTotal = subtotal + gstAmount;

  // Start the CC Avenue flow: ask the backend for the encrypted request, then
  // auto-submit a form to the gateway. The subscription is created/queued only
  // after the gateway confirms payment (server-side callback).
  const confirmPay = async () => {
    if (!selectedPlan) return;
    setLoading(true);
    try {
      const res = await subscriptionApi.initiateCCAvenue(
        companyId,
        selectedPlan.planId,
        selectedAddons,
      );
      const { gatewayUrl, encRequest, accessCode } = res.data || {};
      if (!gatewayUrl || !encRequest || !accessCode) {
        alert(res.data?.message || 'Unable to start payment.');
        setLoading(false);
        return;
      }
      submitToCCAvenue(gatewayUrl, encRequest, accessCode);
      // Browser now navigates to CC Avenue — no further code runs here.
    } catch (err) {
      console.error('Initiate payment failed', err);
      alert(err.response?.data?.message || 'Unable to start payment.');
      setLoading(false);
    }
  };

  // AllUPI — same model as CC Avenue: leave the app entirely, go to AllUPI's
  // QR page; the server callback redirects us back with ?status=...
  const payWithAllUpi = async () => {
    if (!selectedPlan) return;
    setLoading(true);
    try {
      const res = await subscriptionApi.initiateAllUpi(
        companyId,
        selectedPlan.planId,
        selectedAddons,
      );
      const data = res.data || {};
      if (!data.success || !data.paymentUrl) {
        // Mirrors the MVC "Contact to admin" when AllUPI can't initiate.
        alert(data.message || 'Contact to admin');
        setLoading(false);
        return;
      }
      // Full-page redirect to AllUPI's QR page (no iframe / new tab / polling).
      window.location.href = data.paymentUrl;
    } catch (err) {
      console.error('AllUPI initiate failed', err);
      alert(err.response?.data?.message || 'Contact to admin');
      setLoading(false);
    }
  };

  const expired = status?.isExpired;
  const statusColor = expired ? '#f43f5e' : '#10b981';

  return (
    <Container maxWidth='lg' sx={{ mt: 4, mb: 6 }}>
      <Typography variant='h5' sx={{ fontWeight: 700, mb: 3 }}>
        My Subscription
      </Typography>

      {/* ── Payment result banner (CC Avenue return + AllUPI poll) ── */}
      {payResult === 'success' && (
        <Alert severity='success' sx={{ mb: 3 }} onClose={() => setPayResult(null)}>
          Payment successful — your subscription has been updated.
        </Alert>
      )}
      {payResult === 'failed' && (
        <Alert severity='error' sx={{ mb: 3 }} onClose={() => setPayResult(null)}>
          Payment was not completed. No charge was applied — please try again.
        </Alert>
      )}
      {payResult === 'mismatch' && (
        <Alert severity='error' sx={{ mb: 3 }} onClose={() => setPayResult(null)}>
          The amount paid did not match the plan price, so the subscription was
          not activated. Please pay the <strong>exact</strong> plan amount.
        </Alert>
      )}

      {/* ── Current status ── */}
      <Card sx={{ borderRadius: 3, mb: 4, boxShadow: 3 }}>
        <CardContent>
          {!status?.hasSubscription ? (
            <Alert severity='warning'>
              You don't have an active subscription yet. Choose a plan below to
              get started.
            </Alert>
          ) : (
            <Grid container spacing={2} alignItems='center'>
              <Grid item xs={12} md={8}>
                <Typography variant='overline' color='text.secondary'>
                  Current Plan
                </Typography>
                <Typography variant='h6' sx={{ fontWeight: 700 }}>
                  {status.currentPlanName || '—'}{' '}
                  {status.isTrial && (
                    <Chip label='Free Trial' color='info' size='small' />
                  )}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Typography variant='body2'>
                    <strong>Start:</strong> {fmtDate(status.startDate)}
                  </Typography>
                  <Typography variant='body2'>
                    <strong>Expiry:</strong> {fmtDate(status.endDate)}
                  </Typography>
                  <Typography variant='body2'>
                    <strong>Days Left:</strong> {status.daysRemaining}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: { md: 'right' } }}>
                <Chip
                  label={expired ? 'Expired' : 'Active'}
                  sx={{
                    backgroundColor: statusColor,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 14,
                    px: 1,
                  }}
                />
              </Grid>

              {/* Queued plan note — auto-activates on expiry */}
              {status.hasQueuedPlan && (
                <Grid item xs={12}>
                  <Alert severity='success' sx={{ mt: 1 }}>
                    Your next plan <strong>{status.queuedPlanName}</strong> is
                    already purchased and will activate automatically on{' '}
                    <strong>{fmtDate(status.queuedStartDate)}</strong>. No action
                    needed.
                  </Alert>
                </Grid>
              )}

              {expired && (
                <Grid item xs={12}>
                  <Alert severity='error' sx={{ mt: 1 }}>
                    {status.message ||
                      'Subscription Expired. Please Renew Your Plan.'}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* ── Available plans (premium pricing cards) ── */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box
          sx={{
            display: 'inline-block',
            px: 1.6,
            py: 0.4,
            borderRadius: 999,
            bgcolor: '#eef2ff',
            color: '#2563eb',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 1.2,
          }}>
          PRICING PLANS
        </Box>
        <Typography
          sx={{
            mt: 1.5,
            fontSize: { xs: 24, md: 30 },
            fontWeight: 800,
            color: '#0f172a',
            lineHeight: 1.2,
          }}>
          {status?.hasSubscription ? 'Renew or upgrade your ' : 'Choose the perfect '}
          <span style={{ color: '#2563eb' }}>plan</span>
        </Typography>
        <Typography sx={{ mt: 1, color: '#64748b', fontSize: 14 }}>
          Unlock premium features and accelerate your business with our flexible plans.
        </Typography>
      </Box>

      {(() => {
        const myType = sessionStorage.getItem('propertType') || 'H';
        const paid = plans.filter(
          (p) => !p.isTrialPlan && (!p.propertType || p.propertType === myType),
        );
        if (paid.length === 0) {
          return (
            <Alert severity='info' sx={{ mb: 5 }}>
              No plans available right now.
            </Alert>
          );
        }
        const sorted = [...paid].sort(
          (a, b) => (Number(a.price) || 0) - (Number(b.price) || 0),
        );
        const topPrice = Math.max(...sorted.map((p) => Number(p.price) || 0));
        const planIcons = [
          <RocketLaunchRoundedIcon />,
          <WorkspacePremiumRoundedIcon />,
          <BusinessRoundedIcon />,
          <ApartmentRoundedIcon />,
        ];
        return (
          <Box
            sx={{
              mb: 5,
              display: 'grid',
              gap: 2.5,
              alignItems: 'stretch',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0,1fr))',
                lg: `repeat(${Math.min(sorted.length, 4)}, minmax(0,1fr))`,
              },
            }}>
            {sorted.map((p, idx) => {
              const popular = sorted.length > 1 && Number(p.price) === topPrice;
              const intro =
                idx === 0
                  ? 'Everything you need to start'
                  : `All ${sorted[idx - 1].planName} features, plus`;
              return (
                <Box
                  key={p.planId}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 4,
                    p: 3,
                    bgcolor: popular ? '#f3f7ff' : '#fff',
                    border: popular ? '1.5px solid #2563eb' : '1px solid #e6eaf2',
                    boxShadow: popular
                      ? '0 16px 40px rgba(37,99,235,0.16)'
                      : '0 6px 20px rgba(15,23,42,0.05)',
                    transition: 'transform .2s ease, box-shadow .2s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 20px 44px rgba(15,23,42,0.14)',
                    },
                  }}>
                  {popular && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -13,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 999,
                        bgcolor: '#2563eb',
                        color: '#fff',
                        fontSize: 10.5,
                        fontWeight: 800,
                        letterSpacing: 0.6,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 6px 14px rgba(37,99,235,0.4)',
                      }}>
                      <StarRoundedIcon sx={{ fontSize: 13 }} /> MOST POPULAR PLAN
                    </Box>
                  )}

                  {/* Icon badge */}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#eef2ff',
                      color: '#2563eb',
                      mb: 1.5,
                      '& svg': { fontSize: 24 },
                    }}>
                    {planIcons[idx % planIcons.length]}
                  </Box>

                  <Typography sx={{ fontWeight: 800, fontSize: 17, color: '#0f172a' }}>
                    {p.planName}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      color: '#94a3b8',
                      mt: 0.3,
                      minHeight: 34,
                      lineHeight: 1.4,
                    }}>
                    {p.description || 'Flexible plan for your business.'}
                  </Typography>

                  {/* Price */}
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 1.5, mb: 2 }}>
                    <Typography sx={{ fontSize: 30, fontWeight: 900, color: '#2563eb', lineHeight: 1 }}>
                      {currency}
                      {p.price}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#94a3b8' }}>
                      / {p.durationDays} days
                    </Typography>
                  </Box>

                  {/* CTA */}
                  <Button
                    fullWidth
                    onClick={() => openPay(p)}
                    sx={{
                      py: 1.1,
                      borderRadius: 2,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontSize: 14.5,
                      color: '#fff',
                      bgcolor: '#2563eb',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#1d4ed8' },
                    }}>
                    {status?.hasSubscription ? 'Renew / Buy' : 'Choose Plan'}
                  </Button>

                  {/* Features */}
                  <Typography
                    sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155', mt: 2.5, mb: 1.2 }}>
                    {intro}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      maxHeight: 230,
                      overflowY: 'auto',
                      pr: 0.5,
                      '&::-webkit-scrollbar': { width: 4 },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#dbe3f0',
                        borderRadius: 3,
                      },
                    }}>
                    {(p.menuKeys || []).length === 0 && (
                      <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>
                        All standard features included.
                      </Typography>
                    )}
                    {(p.menuKeys || []).map((key) => (
                      <Box
                        key={key}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleRoundedIcon
                          sx={{ fontSize: 17, color: '#2563eb', flexShrink: 0 }}
                        />
                        <Typography sx={{ fontSize: 13, color: '#475569' }}>
                          {MENU_LABELS[key] || key}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      })()}

      {/* ── History ── */}
      <Typography variant='h6' sx={{ fontWeight: 700, mb: 2 }}>
        Subscription History
      </Typography>
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
              <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Start</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>End</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align='center' sx={{ py: 4 }}>
                  No history yet.
                </TableCell>
              </TableRow>
            )}
            {history.map((h) => (
              <TableRow key={h.subscriptionId} hover>
                <TableCell>
                  {h.planName}{' '}
                  {h.isTrialPlan && (
                    <Chip label='Trial' color='info' size='small' />
                  )}
                </TableCell>
                <TableCell>{fmtDate(h.startDate)}</TableCell>
                <TableCell>{fmtDate(h.endDate)}</TableCell>
                <TableCell>
                  {currency}
                  {h.amount}
                </TableCell>
                <TableCell>
                  {h.isCurrent ? (
                    <Chip label='Current' color='success' size='small' />
                  ) : new Date(h.startDate) > new Date() ? (
                    <Chip label='Queued' color='warning' size='small' />
                  ) : (
                    <Chip label='Past' size='small' />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* ── Payment dialog → choose gateway ── */}
      <Dialog open={payOpen} onClose={() => setPayOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Review &amp; Pay</DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Typography variant='body2' sx={{ mb: 1.5 }}>
              Plan: <strong>{selectedPlan.planName}</strong>{' '}
              <span style={{ color: '#64748b' }}>
                ({selectedPlan.durationDays} days)
              </span>
            </Typography>
          )}

          {/* Add-ons (Hotel only) — multi-select, added to the total. */}
          {isHotel && addons.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.75 }}>
                Add-ons (optional)
              </Typography>
              <Box
                sx={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  maxHeight: 180,
                  overflowY: 'auto',
                }}>
                {addons.map((a) => (
                  <Box
                    key={a.addonId}
                    onClick={() => toggleAddon(a.addonId)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1,
                      py: 0.5,
                      cursor: 'pointer',
                      borderBottom: '1px solid #f1f5f9',
                      '&:hover': { bgcolor: '#f8fafc' },
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Checkbox
                        size='small'
                        checked={selectedAddons.includes(a.addonId)}
                        onChange={() => toggleAddon(a.addonId)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Typography sx={{ fontSize: 13 }}>{a.name}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      {currency}
                      {a.price}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Totals: (plan + add-ons) + 18% GST */}
          {selectedPlan && (
            <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.5, mb: 2, fontSize: 14 }}>
              <Row label='Plan' value={`${currency}${planPrice.toFixed(2)}`} />
              {selectedAddons.length > 0 && (
                <Row
                  label={`Add-ons (${selectedAddons.length})`}
                  value={`${currency}${addonSum.toFixed(2)}`}
                />
              )}
              <Row label='Subtotal' value={`${currency}${subtotal.toFixed(2)}`} />
              <Row label='GST (18%)' value={`${currency}${gstAmount.toFixed(2)}`} />
              <Box sx={{ borderTop: '1px dashed #cbd5e1', mt: 0.5, pt: 0.5 }}>
                <Row
                  label={<strong>Total payable</strong>}
                  value={
                    <strong style={{ color: '#1A56DB' }}>
                      {currency}
                      {grandTotal.toFixed(2)}
                    </strong>
                  }
                />
              </Box>
            </Box>
          )}
          {status?.hasSubscription && !expired && (
            <Alert severity='info' sx={{ mb: 2 }}>
              Your current plan is still active — this purchase will be queued
              and start automatically when it expires.
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <Button
              variant='contained'
              size='large'
              fullWidth
              onClick={confirmPay}
              disabled={loading}
              sx={{ background: '#1A56DB' }}>
              {loading ? 'Please wait…' : 'Pay with CC Avenue (Card / UPI / Netbanking)'}
            </Button>
            <Button
              variant='contained'
              size='large'
              fullWidth
              onClick={payWithAllUpi}
              disabled={loading}
              sx={{ background: 'linear-gradient(135deg,#16a34a,#22c55e)' }}>
              {loading ? 'Please wait…' : 'Pay with AllUPI (UPI QR)'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)} disabled={loading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
